import React, { useEffect, useState, useRef } from "react";
import { async, hash } from "rsvp";
import moment from "moment";
import { toast } from "react-toastify";
import {
  AnimalCounts,
  AnimalIntakes,
  AnimalOutcomes,
  AnimalEntryDetails,
} from "./components";
import LoadingIndicator from "components/LoadingIndicator";
import Error from "components/Error";
import { useQuery, useQueryClient, useMutation } from "react-query";
import { useParams } from "react-router-dom";
import { useAuthState, useAuthDispatch, useUserState } from "context";
import {
  apiFetch,
  API_URL,
  formatUiErrors,
  useHandleRequestStates,
} from "utils";
import {
  generatePayload,
  updateSpeciesCache,
} from "./components/AnimalEntryDetails/utils";
import Button from "components/Button";

const DataEntry = () => {
  const { token } = useAuthState();
  const dispatch = useAuthDispatch();
  const currentMoment = moment();
  const {
    year: urlYear,
    month: urlMonth,
    location,
    organizationId,
  } = useParams();
  const year = urlYear || currentMoment.format("YYYY");
  const month = urlMonth || currentMoment.format("M");
  const queryClient = useQueryClient();
  const [animalSpecies, setAnimalSpecies] = useState(["canine", "feline"]);
  const [selectedSpecies, setSelectedSpecies] = useState("");
  const [recalculate, setRecalculate] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [submittedAll, setSubmittedAll] = useState(false);
  const [speciesMonthEndingCount, setSpeciesMonthEndingCount] = useState({});
  const [newOrganization, setOrganization] = useState({});
  const [newlocation, setNewLocation] = useState({});

  const animalIntakeForm = useRef(null);
  const animalOutcomeForm = useRef([]);
  const animalCountForm = useRef(null);

  const { attributes, isAdmin, organization, isOrgAdmin } = useUserState();
  var locationId = attributes?.locationId;
  if (isAdmin && location) {
    locationId = location;
  }

  const getOrg = async () => {
    try {
      if (organizationId) {
        const response = await apiFetch({
          token,
          dispatch,
          endpoint: `/api/v1/organizations/${organizationId}`,
        });
        if (response?.data) {
          setOrganization(response.data);
        }
      }
      if (locationId) {
        const response2 = await apiFetch({
          token,
          dispatch,
          endpoint: `/api/v1/locations/${locationId}`,
        });
        if (response2?.data) {
          setNewLocation(response2.data);
        }
      }
    } catch (error) {
      toast.error("Failed to load organization/location", error);
    }
  };

  useEffect(() => {
    getOrg();
  }, []);

  const { handleSuccess } = useHandleRequestStates();

  const generateDataEntries = ({
    locationId,
    year,
    month,
    token,
    dispatch,
  }) => {
    const dateFilters = `filter[year_of_record][eq]=${year}&filter[month_of_record][eq]=${month}`;
    const statusDateFilters = `filter[year][eq]=${year}&filter[month][eq]=${month}`;
    const locationFilter = `&filter[location_id][eq]=${locationId}`;

    return hash({
      status: apiFetch({
        token,
        dispatch,
        endpoint: `/api/v1/data_entry_status?${statusDateFilters}${locationFilter}`,
      }),
      counts: apiFetch({
        token,
        dispatch,
        endpoint: `/api/v1/animal_counts?${dateFilters}${locationFilter}`,
      }),
      intakes: apiFetch({
        token,
        dispatch,
        endpoint: `/api/v1/animal_intakes?${dateFilters}${locationFilter}`,
      }),
      outcomes: apiFetch({
        token,
        dispatch,
        endpoint: `/api/v1/animal_outcomes?${dateFilters}${locationFilter}`,
      }),
    });
  };

  const {
    mutate: setMatrixSpecies,
    isLoading: isLoadingTables,
    error: isTableError,
  } = useMutation(
    () => {
      const payload = generatePayload({
        addedSpecies: animalSpecies,
        removedSpecies: [],
        locationId,
        year,
        month,
      });
      return apiFetch({
        token,
        dispatch,
        endpoint: `/api/v1/locations/${locationId}`,
        method: "PATCH",
        body: {
          data: {
            id: locationId,
            type: "locations",
            attributes: {},
            relationships: payload.relationships,
          },
          included: payload.included,
        },
      });
    },
    {
      onSuccess: (response) => {
        return queryClient.setQueryData(
          ["dataEntry", `${locationId}_${month}/${year}`],
          (oldData) =>
            updateSpeciesCache({
              oldData,
              response,
              changedSpecies: { added: animalSpecies, removed: [] },
            })
        );
      },
    }
  );

  // Calculate number of species in care at the end of the month
  const calculateEndingCount = () => {
    if (!response) return;

    const intakes = response.intakes?.data || [];
    const outcomes = response.outcomes?.data || [];
    const counts = response.counts?.data || [];

    let intakeHash = {};
    let outcomeHash = {};

    intakes.forEach((intake) => {
      if (intake?.attributes) {
        intakeHash[intake.attributes.species] =
          intake.attributes.totalLiveIntakeCount || 0;
      }
    });

    outcomes.forEach((outcome) => {
      if (outcome?.attributes) {
        outcomeHash[outcome.attributes.species] =
          outcome.attributes.totalOutcomesCount || 0;
      }
    });

    let countsHash = {};
    animalSpecies.forEach((species) => {
      counts.forEach((count) => {
        if (count?.attributes && count.attributes.species === species) {
          const beginningCount = count.attributes.beginningAnimalCount || 0;
          const intakeCount = intakeHash[species] || 0;
          const outcomeCount = outcomeHash[species] || 0;
          const endCount = count.attributes.endingAnimalCount;

          const total = beginningCount + (intakeCount - outcomeCount);

          Object.assign(
            countsHash,
            endCount === 0 || !endCount
              ? {
                  [species]: total < 0 ? 0 : total,
                }
              : {
                  [species]: count.attributes.endingAnimalCount,
                }
          );
        }
      });
    });

    setSpeciesMonthEndingCount(countsHash);
  };

  // General Request
  const {
    isLoading,
    error,
    data: response,
    refetch,
  } = useQuery(
    ["dataEntry", `${locationId}_${month}/${year}`],
    () => {
      if (!locationId) {
        return Promise.resolve(null);
      }
      return generateDataEntries({ locationId, year, month, token, dispatch });
    },
    {
      enabled: !!locationId,
      onSuccess: (response) => {
        if (!response) return;

        const countsData = response.counts?.data || [];
        const intakesData = response.intakes?.data || [];
        const outcomesData = response.outcomes?.data || [];

        // IF Counts & Intakes & Outcomes have no data we need to set up our species
        if (!countsData.length && !intakesData.length && !outcomesData.length) {
          if (
            organizationId &&
            newOrganization?.attributes &&
            newOrganization.attributes.animalSpecies &&
            newOrganization.attributes.animalSpecies.length !== 0
          ) {
            setAnimalSpecies(newOrganization.attributes.animalSpecies);
          } else if (
            organization &&
            organization.attributes &&
            organization.attributes.animalSpecies &&
            organization.attributes.animalSpecies.length !== 0
          ) {
            setAnimalSpecies(organization.attributes.animalSpecies);
          }
        } else {
          const dataToSpeciesArray = ["counts", "intakes", "outcomes"].reduce(
            (speciesArray, key) => {
              const subArray = response[key]?.data || [];

              subArray.forEach((d) => {
                const { attributes } = d || {};
                const { species } = attributes || {};

                if (species && speciesArray.indexOf(species) === -1) {
                  speciesArray.push(species);
                }
              });

              return speciesArray;
            },
            []
          );

          setAnimalSpecies(dataToSpeciesArray);
        }
      },
    }
  );

  useEffect(() => {
    if (!response || !locationId) {
      return;
    }

    const countsData = response.counts?.data || [];
    const intakesData = response.intakes?.data || [];
    const outcomesData = response.outcomes?.data || [];

    if (
      !countsData.length &&
      !intakesData.length &&
      !outcomesData.length &&
      isOrgAdmin
    ) {
      setMatrixSpecies();
    } else {
      calculateEndingCount();
    }
  }, [response, setMatrixSpecies, locationId, isOrgAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (locationId) {
      refetch();
    }
  }, [locationId, submittedAll, refetch]);

  useEffect(() => {
    if (locationId) {
      setTimeout(() => {
        refetch();
      }, 1000);
    }
  }, [isEditing, locationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    try {
      if (animalIntakeForm.current?.handleSubmit) {
        await animalIntakeForm.current.handleSubmit();
      }
      if (animalOutcomeForm.current?.liveOutcome?.handleSubmit) {
        await animalOutcomeForm.current.liveOutcome.handleSubmit();
      }
      if (animalOutcomeForm.current?.otherOutcome?.handleSubmit) {
        await animalOutcomeForm.current.otherOutcome.handleSubmit();
      }
      if (animalCountForm.current?.handleSubmit) {
        await animalCountForm.current.handleSubmit();
      }
      setIsEditing(false);
    } catch (error) {
      toast.error("Failed to save data", error);
    }
  };

  const setSpecies = (species) => {
    if (species === selectedSpecies) {
      setSelectedSpecies("");
    } else {
      setSelectedSpecies(species);
    }
  };

  const autoRecalculate = (key, val) => {
    if (
      !animalIntakeForm.current ||
      !animalOutcomeForm.current ||
      !animalCountForm.current
    ) {
      return;
    }

    if (key === "endingAnimalCount") {
      const { values: intakes = {} } = animalIntakeForm.current;
      const { values: liveOutcomes = {} } =
        animalOutcomeForm.current.liveOutcome || {};
      const { values: otherOutcomes = {} } =
        animalOutcomeForm.current.otherOutcome || {};
      const { values: countValues = {} } = animalCountForm.current;

      var newIntakes = Object.keys(intakes)
        .filter(
          (a) =>
            ![
              "locationId",
              "species",
              "monthOfRecord",
              "yearOfRecord",
            ].includes(a)
        )
        .reduce((cur, key) => {
          return Object.assign(cur, { [key]: intakes[key] || 0 });
        }, {});
      var newIntakesSum = Object.values(newIntakes)
        .filter((a) => a >= 0)
        .reduce((a, b) => a + b, 0);

      var newLiveOutCome = Object.keys(liveOutcomes)
        .filter(
          (a) =>
            ![
              "locationId",
              "species",
              "monthOfRecord",
              "yearOfRecord",
            ].includes(a)
        )
        .reduce((cur, key) => {
          return Object.assign(cur, { [key]: liveOutcomes[key] || 0 });
        }, {});
      var newliveOutComeSum = Object.values(newLiveOutCome)
        .filter((a) => a >= 0)
        .reduce((a, b) => a + b, 0);

      var newotherOutcomes = Object.keys(otherOutcomes)
        .filter(
          (a) =>
            ![
              "locationId",
              "species",
              "monthOfRecord",
              "yearOfRecord",
            ].includes(a)
        )
        .reduce((cur, key) => {
          return Object.assign(cur, { [key]: otherOutcomes[key] || 0 });
        }, {});
      var newotherOutcomesSum = Object.values(newotherOutcomes)
        .filter((a) => a >= 0)
        .reduce((a, b) => a + b, 0);

      var total = newliveOutComeSum + newotherOutcomesSum;

      val = (countValues.beginningAnimalCount || 0) + newIntakesSum - total;
    }

    if (animalCountForm.current.setFieldValue) {
      animalCountForm.current.setFieldValue(key, val || 0);
    }
  };

  const handleSubmitAll = () => {
    setSubmittedAll(!submittedAll);
    handleSuccess({
      message: "All data submitted.",
    });
  };

  const Content = () => {
    if (!locationId) {
      return (
        <Error>
          Unable to determine location. Please check your access permissions.
        </Error>
      );
    }

    if (isLoading || isLoadingTables) {
      return (
        <LoadingIndicator inline content={<>Loading data matrix&hellip;</>} />
      );
    }

    if (error || isTableError) {
      return <Error>{formatUiErrors(error || isTableError)}</Error>;
    }

    if (newlocation?.attributes?.status === "closed") {
      return (
        <div>
          <b>{newlocation.attributes?.name || "Location"}</b> - location is
          closed by system admin. If you want to enter data for this location
          please contact Shelter Animals Count helpdesk,{" "}
          <a href="mailto:info@shelteranimalscount.org">
            info@shelteranimalscount.org
          </a>
        </div>
      );
    }

    if (!response) {
      return <Error>No data available</Error>;
    }

    const statusData = response.status?.data || [];
    const entryStatus = statusData[0]?.attributes?.status || "unknown";

    return (
      <>
        {/* Entry Status at this level will only ever return 1 item so we just grab the 0 index */}
        <AnimalEntryDetails
          defaultSpecies={animalSpecies}
          entryStatus={entryStatus}
          selectedSpecies={selectedSpecies}
          setSpecies={setSpecies}
          setIsEditing={setIsEditing}
          isEditing={isEditing}
          handleSubmit={handleSubmit}
          location={newlocation}
          handleSubmitAll={handleSubmitAll}
        />
        <AnimalIntakes
          animalData={response.intakes?.data || []}
          selectedSpecies={selectedSpecies}
          isEditing={isEditing}
          ref={animalIntakeForm}
        />
        <AnimalOutcomes
          animalData={response.outcomes?.data || []}
          selectedSpecies={selectedSpecies}
          isEditing={isEditing}
          ref={animalOutcomeForm}
        />
        <AnimalCounts
          animalData={response.counts?.data || []}
          selectedSpecies={selectedSpecies}
          isEditing={isEditing}
          ref={animalCountForm}
          recalculate={recalculate}
          setRecalculate={autoRecalculate}
          endingCounts={speciesMonthEndingCount}
        />
        {selectedSpecies && (
          <div className="sac-border flex gap-1 justify-end">
            <Button
              key="edit"
              disabled={isEditing}
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
            <Button key="submit" disabled={!isEditing} onClick={handleSubmit}>
              Save
            </Button>
          </div>
        )}
      </>
    );
  };

  return (
    <div>
      <h1 className="mb-0">Enter Shelter Intake and Outcome Data</h1>
      <p className="mb-10">
        Please keep in mind that all data fields must be filled in, even if the
        entry is "0." Records will be considered incomplete until all fields are
        populated with numeric values.
      </p>

      <Content />
    </div>
  );
};

export default DataEntry;
